import {
  addDays,
  batchTimeline,
  CATEGORIES,
  CATEGORY_EMOJI,
  CATEGORY_LABEL,
  type CartEntry,
  type Category,
  daysBetween,
  describeDaysLeft,
  describeHistory,
  type ExpiryPrecision,
  findSimilar,
  formatIsoDate,
  formatMoney,
  formatQuantity,
  fractionElapsed,
  guessQuantity,
  type IsoDate,
  isIsoDate,
  isOnCart,
  isoFromMillis,
  isRightOne,
  itemEmoji,
  parseRupees,
  QUICK_EXPIRY,
  type Quantity,
  STORAGE_ICON,
  STORAGE_LABEL,
  type StoragePlace,
  storageLabel,
  unitPrice,
  urgencyOf,
} from "@larder/shared";
import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { EmojiField } from "../components/EmojiField";
import { NutritionPanel } from "../components/NutritionPanel";
import { QuantityField } from "../components/QuantityField";
import type { TrackedItem } from "../data/items";

/**
 * How long a pause counts as "finished".
 *
 * Long enough that typing a name is one write rather than fifteen; short enough
 * that tapping a chip and immediately hitting Back still lands. Chips and
 * steppers could fire instantly, but a single timer for every field keeps the
 * status line honest — two save paths would let it say "Saved" while a
 * half-typed name was still pending.
 */
const AUTOSAVE_MS = 600;

/** How long the popup takes to slide back down. Matches `popup-down` in CSS. */
const POPUP_EXIT_MS = 180;

/**
 * How long a row stays flagged as "this is different on the packet you just
 * opened". Matches `row-changed` in CSS, plus enough to be noticed.
 */
const CHANGED_MS = 1400;

/** Shared, so an unchanged render is not allocating an empty Set every time. */
const NOTHING_CHANGED: ReadonlySet<never> = new Set();

/** The rows of the facts card, and the anchors edit mode focuses. */
type Field = "size" | "expiry" | "storage" | "category" | "price";

/** `size` is an object, so identity is not the question — the two numbers are. */
const sameQuantity = (a: Quantity | null, b: Quantity | null) =>
  a?.amount === b?.amount && a?.unit === b?.unit;

/**
 * Local editing state that ADOPTS a value arriving from the server, unless the
 * user has changed it here.
 *
 * Plain `useState(item.category)` seeds once and then ignores the item forever,
 * which is wrong on this screen for a specific and reachable reason:
 * enrichment lands a second or two after an item is added, and people tap the
 * thing they just added. The screen would then be holding `category: null`
 * while the item had gained one — permanently "Unsaved…", and its next autosave
 * would write those nulls straight back over the category, emoji and size that
 * enrichment had just filled in. The feature that fills blanks in would be
 * undone by looking at the item.
 *
 * The rule is: if the server value moved and the local value is still the old
 * server value, the user never touched it, so take the new one. If the local
 * value differs, the user DID touch it and it wins — which is the same
 * precedence `applyEnrichment` uses from the other direction.
 *
 * Setting state during render is React's documented way to adjust state when a
 * prop changes; it re-renders immediately without painting the stale value.
 */
function useAdopted<T>(
  incoming: T,
  same: (a: T, b: T) => boolean = Object.is,
): [T, (next: T) => void] {
  const [local, setLocal] = useState(incoming);
  const seen = useRef(incoming);

  if (!same(seen.current, incoming)) {
    const untouched = same(local, seen.current);
    seen.current = incoming;
    if (untouched) setLocal(incoming);
  }

  return [local, setLocal];
}

/**
 * One item, opened.
 *
 * This screen used to be a form: six controls in a card, and everything the app
 * actually KNEW about the item either buried below them or not shown at all. It
 * is now the other way round. What is known leads — what is in it, how many of
 * them you have, what has happened to it — and the controls are one tap away
 * behind a summary that states the same facts in a fifth of the space.
 *
 * The order is the order of the questions people arrive with:
 *
 *   1. Which of these should I open?   (only when there is more than one)
 *   2. What is in it?
 *   3. Are the details right?          (summary; edit on demand)
 *   4. What has happened to it?
 *   5. I have finished with it.        (pinned, always in reach)
 *
 * Everything still AUTOSAVES — there is no Save button. See the effect below.
 */
export function ItemDetail({
  item,
  all,
  places,
  today,
  timezone,
  cart,
  onSave,
  onAnswer,
  onBuyAgain,
  onOpen,
  onClose,
  busy,
  error,
}: {
  item: TrackedItem;
  all: TrackedItem[];
  places: StoragePlace[];
  today: IsoDate;
  /** The household's zone. Event instants are dated in it, never in the browser's. */
  timezone: string;
  cart: CartEntry[];
  onSave: (patch: {
    name: string;
    expiryDate: IsoDate | null;
    expiryPrecision: ExpiryPrecision;
    storageId: string | null;
    size: Quantity | null;
    emoji: string | null;
    category: Category | null;
    priceMinor: number | null;
  }) => void;
  onAnswer: (type: "ate" | "tossed" | "still_here") => void;
  onBuyAgain: () => void;
  onOpen: (id: string) => void;
  onClose: () => void;
  busy: boolean;
  /**
   * Why the last save failed, if it did.
   *
   * This screen had no way to say so, and the omission hid a real bug: an
   * `undefined` field reaching `updateDoc` throws, the error was caught and
   * stored one level up, and the only symptom was a Save button that did
   * nothing at all. A write that can fail needs somewhere to say it failed.
   */
  error: string | null;
}) {
  const [name, setName] = useAdopted(item.name);
  const [expiry, setExpiry] = useAdopted<IsoDate | null>(item.derived.effectiveExpiry);
  /**
   * How precisely the date is known.
   *
   * Tracked as state rather than read straight off the item because ANY edit
   * made here is day-precise by construction — a date picker cannot express
   * "March 2028". Leaving the stored precision alone would keep rendering a
   * date the user chose to the day as "Mar 2028", which is the fabrication rule
   * (AGENTS.md 2) running backwards: claiming LESS than we know.
   */
  const [precision, setPrecision] = useAdopted<ExpiryPrecision>(item.expiryPrecision);
  const [storageId, setStorageId] = useAdopted<string | null>(item.storageId);
  const [size, setSize] = useAdopted<Quantity | null>(item.size, sameQuantity);
  const [emoji, setEmoji] = useAdopted<string | null>(item.emoji);
  const [category, setCategory] = useAdopted<Category | null>(item.category);
  /**
   * Price is held as TEXT while it is being typed.
   *
   * "68." is a valid thing to have typed and not a valid price, and parsing on
   * every keystroke would delete the decimal point out from under the user.
   */
  const [priceText, setPriceText] = useState(
    item.priceMinor === null ? "" : String(item.priceMinor / 100),
  );
  const priceMinor = parseRupees(priceText);

  /**
   * Whether the edit popup is open.
   *
   * The facts are always visible; editing happens OVER them rather than instead
   * of them. Swapping the card in place made the page jump — five short rows
   * become five fieldsets, so everything below moved a screen and a half — and
   * it hid the values you were correcting against at the moment you needed them
   * most. A popup keeps the summary where it was and gives the controls the room
   * they actually need.
   *
   * The single most common edit is fixing a wrong date, so every summary row is
   * its own way in and opens with that field focused. The Edit button is the
   * discoverable path; the row is the fast one.
   */
  /**
   * Switching to a sibling packet, without redrawing the screen.
   *
   * App used to key this component on the item id, which remounted everything
   * and replayed the staggered entrance. Correct, and far too blunt: two
   * packets of the same product differ in a date and a shelf, so nine cards
   * animated in to report that two rows had changed.
   *
   * So the DOM stays put and the screen re-seeds itself instead. Two jobs, and
   * the second is the point:
   *
   *   reset every field to the packet now being shown, and
   *   work out which of them the reader would otherwise miss.
   *
   * `useAdopted` already adopts an untouched field on its own; this runs over
   * the top of it because a field the user HAD edited must still follow the
   * switch — it belongs to the packet they left, not to this one.
   */
  const shownId = useRef(item.id);
  const lastItem = useRef(item);
  const [changed, setChanged] = useState<ReadonlySet<Field>>(NOTHING_CHANGED);

  useEffect(() => {
    lastItem.current = item;
  });

  const [editing, setEditing] = useState(false);
  /**
   * Held open, briefly, so it can leave the way it arrived.
   *
   * Unmounting on the tap makes the panel vanish, and a panel that vanishes
   * reads as a crash rather than as a dismissal — the eye has nothing to
   * follow. `closing` keeps it mounted for exactly the length of the slide
   * down, and the timer is the only thing that finally removes it.
   */
  const [closing, setClosing] = useState(false);
  const [focusField, setFocusField] = useState<Field | null>(null);
  const factsRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  if (shownId.current !== item.id) {
    // The packet we are leaving, as it was at the last commit — so a value
    // edited while it was open still counts as its own, not as this one's.
    const before = lastItem.current;
    shownId.current = item.id;

    const diff = new Set<Field>();
    if (before.derived.effectiveExpiry !== item.derived.effectiveExpiry) {
      diff.add("expiry");
    }
    if (before.storageId !== item.storageId) diff.add("storage");
    if (!sameQuantity(before.size, item.size)) diff.add("size");
    if (before.category !== item.category) diff.add("category");
    if (before.priceMinor !== item.priceMinor) diff.add("price");
    setChanged(diff);

    setName(item.name);
    setExpiry(item.derived.effectiveExpiry);
    setPrecision(item.expiryPrecision);
    setStorageId(item.storageId);
    setSize(item.size);
    setEmoji(item.emoji);
    setCategory(item.category);
    setPriceText(item.priceMinor === null ? "" : String(item.priceMinor / 100));
    // A panel opened against the old packet has no business editing the new one.
    setEditing(false);
    setClosing(false);
  }

  /**
   * The flash is a pointer, not a state — so it has to stop pointing.
   *
   * Left on, it would still be highlighting the date an hour later, and a
   * highlight that never clears stops meaning "this just changed".
   */
  useEffect(() => {
    if (changed.size === 0) return;
    const t = setTimeout(() => setChanged(NOTHING_CHANGED), CHANGED_MS);
    return () => clearTimeout(t);
  }, [changed]);

  const alreadyCarted = isOnCart(item.name, cart);
  const dirty =
    name.trim() !== item.name ||
    expiry !== item.derived.effectiveExpiry ||
    precision !== item.expiryPrecision ||
    storageId !== item.storageId ||
    emoji !== item.emoji ||
    category !== item.category ||
    priceMinor !== item.priceMinor ||
    // Compared field-by-field, not by reference: `size` is an object, and a
    // stepper that lands back on the value it started from has changed nothing.
    !sameQuantity(size, item.size);

  /**
   * Every packet of this thing, soonest first.
   *
   * The list screen collapses duplicates behind a `×2`, which is right there and
   * leaves "which one do I open?" with nowhere to be answered. Here is where it
   * gets answered — and it is the first thing on the screen when it applies,
   * because it decides which item you should be looking at at all.
   */
  const slots = useMemo(() => {
    const dated = (i: TrackedItem) => ({ ...i, expiry: i.derived.effectiveExpiry });
    return batchTimeline(
      dated(item),
      findSimilar(item, all)
        // A packet already eaten or thrown is not one you can go and open.
        .filter((o) => o.derived.status !== "consumed" && o.derived.status !== "wasted")
        .map(dated),
    );
  }, [item, all]);
  const rightOne = isRightOne(slots);

  const history = useMemo(
    () => describeHistory(item.events, timezone),
    [item.events, timezone],
  );

  /**
   * The run from the day it arrived to the day it goes off, and how much of it
   * is gone.
   *
   * Null when there is nothing to draw: no date at all, or an item already
   * eaten or thrown, whose story has an ending and does not need a future
   * pencilled in below it.
   *
   * Measured from the `added` EVENT rather than from `createdAt`, so the two
   * ends of the line are both calendar days in the household's zone (ADR 0009)
   * rather than one day and one instant. `createdAt` is the fallback for the
   * items old enough to predate the event, not the preference.
   */
  const life = useMemo(() => {
    if (!expiry) return null;
    if (item.derived.status !== "active" && item.derived.status !== "awaiting") {
      return null;
    }
    const added = item.events.find((e) => e.type === "added");
    const from = isoFromMillis(added?.at ?? item.createdAt, timezone);
    return {
      on: expiry,
      spent: fractionElapsed(from, expiry, today),
      urgency: urgencyOf(daysBetween(today, expiry), item.derived.status),
    };
  }, [expiry, item.events, item.createdAt, item.derived.status, timezone, today]);

  /**
   * AUTOSAVE. There is no Save button, because every control on this screen is
   * a tap that already expresses a decision.
   *
   * A Save button asks you to state an intention you have already stated: you
   * did not pick "🍫" and then wonder whether you meant it. It also creates a
   * state the app has no answer for — edits made and then navigated away from —
   * which is the state people actually hit, because tapping a chip *feels* like
   * it committed.
   *
   * Debounced rather than immediate, and one timer for every field, because the
   * name is typed. Saving per keystroke would be a write per character; waiting
   * for a blur would miss the person who taps Back. A pause is the signal that
   * a thought is finished, whichever control produced it.
   */
  const saveRef = useRef(onSave);
  saveRef.current = onSave;
  /** The write the timer is waiting to make, or null if there is nothing due. */
  const pendingRef = useRef<Parameters<typeof onSave>[0] | null>(null);

  useEffect(() => {
    // An empty name is not a decision, it is a field mid-edit. Saving it would
    // erase the only thing identifying the item.
    if (!dirty || !name.trim()) {
      pendingRef.current = null;
      return;
    }

    const patch = {
      name: name.trim(),
      expiryDate: expiry,
      expiryPrecision: precision,
      storageId,
      size,
      emoji,
      category,
      priceMinor,
    };
    pendingRef.current = patch;

    const t = setTimeout(() => {
      pendingRef.current = null;
      // Read through a ref so a new inline `onSave` closure on every parent
      // render cannot restart the timer and postpone the save indefinitely.
      saveRef.current(patch);
    }, AUTOSAVE_MS);

    return () => clearTimeout(t);
  }, [dirty, name, expiry, precision, storageId, size, emoji, category, priceMinor]);

  /**
   * Flushes a half-second-old edit that the user walked away from.
   *
   * The debounce above promised that "tapping a chip and immediately hitting
   * Back still lands", and it did not: leaving inside the 600ms clears the
   * timer along with the component, and the edit was simply gone. The screen
   * hid it, because the status line used to sit on "✓ Saved" — which it had
   * been saying since before the tap.
   *
   * Runs on unmount only, and after the timer's own cleanup, so the two cannot
   * both fire: a timer that already ran left `pendingRef` empty.
   */
  useEffect(
    () => () => {
      const pending = pendingRef.current;
      if (pending) saveRef.current(pending);
    },
    [],
  );

  /**
   * Puts the cursor where the tap said to put it.
   *
   * Opening the popup at the top and making someone hunt for the field they
   * just tapped would make the summary a slower route to the date than the old
   * always-open form was, which would defeat the whole rearrangement.
   *
   * Falls back to focusing the panel itself, so opening from the Edit button
   * still moves focus off the page behind — a dialog that leaves the keyboard
   * outside it is a dialog only the mouse can use.
   *
   * ONCE per opening, which the `placed` ref is entirely there to enforce.
   * Clearing `focusField` re-runs this effect, and on that second pass there is
   * no field to find — so the fallback fired and pulled focus off the date
   * input it had just placed it on, one tick earlier. The visible symptom was a
   * popup that opened on the right row with nothing focused.
   */
  const placed = useRef(false);

  useEffect(() => {
    if (!editing) {
      placed.current = false;
      return;
    }
    if (placed.current) return;
    placed.current = true;

    const group = focusField
      ? factsRef.current?.querySelector<HTMLElement>(`[data-field="${focusField}"]`)
      : null;
    setFocusField(null);

    if (!group) {
      popupRef.current?.focus();
      return;
    }
    group.scrollIntoView({ block: "center", behavior: "smooth" });
    group.querySelector<HTMLInputElement>("input, select, button")?.focus();
  }, [editing, focusField]);

  /**
   * Escape closes it, because every popup on every platform does.
   *
   * Bound to the document rather than the panel: the focused element may be a
   * native date picker, whose key handling is the browser's and not ours.
   */
  useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeEdit();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  /**
   * Runs the exit animation, then unmounts.
   *
   * A timer rather than `animationend`, because the reduced-motion reset
   * shortens every animation to 0.01ms — the event still fires, but relying on
   * it would make dismissal depend on an animation the user has asked not to
   * have. The timer is capped by the same setting arriving at the same answer:
   * the panel is gone either way, just sooner.
   */
  const closeEdit = () => {
    setClosing(true);
    setTimeout(() => {
      setEditing(false);
      setClosing(false);
    }, POPUP_EXIT_MS);
  };

  const openEdit = (field: Field | null) => {
    setClosing(false);
    setEditing(true);
    setFocusField(field);
  };

  /**
   * What the save is doing, WHEN it is doing something.
   *
   * This used to sit at "✓ Saved" permanently, on the theory that a screen with
   * no Save button owes you a confirmation. In practice a tick that is always
   * lit confirms nothing — it is on before you touch anything, so it cannot be
   * the answer to a tap — and it reads as decoration rather than as state.
   *
   * Silence is the resting state now. The line speaks only while a write is in
   * flight, or when one has failed, which is the case that genuinely has
   * nowhere else to be reported (an `undefined` field reaching `updateDoc`
   * throws, and its only other symptom is a control that did nothing).
   *
   * One element, moved rather than duplicated: it belongs wherever the editing
   * is happening — inside the popup while it is open, on the page otherwise,
   * since the name is edited in place at the top.
   */
  const status = error ? (
    <p className="hint" data-testid="edit-error">
      {error}
    </p>
  ) : busy ? (
    <p className="save-state" role="status" aria-live="polite" data-testid="edit-status">
      Saving…
    </p>
  ) : null;

  const where = storageLabel(storageId, places, item.storage, STORAGE_LABEL);
  const daysLeft = expiry ? daysBetween(today, expiry) : null;
  const urgency = urgencyOf(daysLeft, item.derived.status);

  return (
    /* App keys this screen on the item id, so opening one packet from another's
       timeline remounts rather than swapping content in place: every `useState`
       above seeds from `item`, and without the remount they would keep the
       previous packet's values. It also replays the entrance animation, which
       is what makes the move read as a move. */
    <section aria-label="item" className="detail">
      {/* Back sits ON the title's row, not in a band of its own above it.
          Floating alone at the top of the screen it read as orphaned — a
          control belonging to nothing in particular. Beside the title it is
          plainly the way back out of THIS thing. */}
      <header className="detail-head">
        {/* Icon only — but the NAME still has to exist for anyone not looking
            at it. An arrow with no accessible name is a button screen readers
            announce as "button", which is the one thing it must never be. */}
        <button
          type="button"
          className="back"
          data-testid="back-btn"
          onClick={onClose}
          aria-label="Back"
        >
          <span aria-hidden="true">←</span>
        </button>
        {/* The glyph sits with the identity, because that is what it is — the
            picture of this thing, beside its name. Tapping it opens the picker;
            it is the one piece of enrichment the user can overrule directly. */}
        <EmojiField value={emoji} category={category} onChange={setEmoji} />
        <div className="detail-ident">
          {/* The title IS the name field. It used to be a heading, with a
              separate "Name" input in the card below repeating it — the same
              string twice on one short screen, and a rename that happened in
              one place while you were looking at the other. */}
          <input
            className="detail-title-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Name"
            data-testid="edit-name"
          />
        </div>
        {/* At the END of the row, not under the name.
            Indented beneath the title it sat against nothing — left-aligned to
            the name's text but belonging to no column, which is exactly what
            "hanging out of nowhere" looks like. On the row's right edge it is
            anchored, and it lines up with the days-left pill on every list row,
            which is where the eye already looks for this fact.

            The days-left phrase, not the date: it is what decides what you do
            next, and the date itself is one row down in the summary. */}
        <span className={`when ${urgency}`} data-testid="detail-when">
          {daysLeft === null ? "No date" : describeDaysLeft(daysLeft)}
        </span>
      </header>

      {/* ── 1. Which one should I open? ─────────────────────────────────
          Only when there IS more than one. Two boxes of Maggi are one product
          and two records, and this is the only place the difference between
          them can be acted on. */}
      {slots.length > 1 && (
        <div className="card batch" data-testid="batch">
          <h3 className="card-title">
            You have <span className="count">{slots.length}</span>
          </h3>
          <ol className="timeline">
            {slots.map((slot) => {
              const other = slot.item;
              const left = other.derived.daysLeft;
              const tone = urgencyOf(left, other.derived.status);
              return (
                <li key={other.id} className={`tl ${slot.current ? "on" : ""}`}>
                  <button
                    type="button"
                    className="tl-row"
                    aria-current={slot.current ? "true" : undefined}
                    /* The open packet is not a link to itself. */
                    disabled={slot.current}
                    onClick={() => onOpen(other.id)}
                    data-testid={`batch-${other.id}`}
                  >
                    <span className={`tl-dot ${tone}`} aria-hidden="true" />
                    <span className="tl-date">
                      {other.derived.effectiveExpiry
                        ? formatIsoDate(
                            other.derived.effectiveExpiry,
                            other.expiryPrecision,
                          )
                        : "No date"}
                    </span>
                    <span className={`when ${tone}`}>
                      {left === null ? "—" : describeDaysLeft(left)}
                    </span>
                    {/* At most one tag per row, and only where it changes what
                        the user does. Labelling every row would turn a glance
                        into a paragraph. */}
                    {slot.useFirst && slot.current && (
                      <span className="tl-tag good">Use this one</span>
                    )}
                    {slot.useFirst && !slot.current && (
                      <span className="tl-tag good">Use first</span>
                    )}
                    {!slot.useFirst && slot.current && (
                      <span className="tl-tag">This one</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ol>
          {/* One line, and only when it is a correction. Confirming that the
              open packet is already the right one would be a sentence saying
              nothing — the "Use this one" tag has said it. */}
          {!rightOne && (
            <p className="note" data-testid="batch-hint">
              An older one is still open.
            </p>
          )}
        </div>
      )}

      {/* ── 2. What is in it? ────────────────────────────────────────────
          Promoted to the top of the facts, because it is the only thing on this
          screen the user could not have worked out for themselves. */}
      {item.nutrition && (
        <div className="card">
          {/* Named, not "in it". By the time this heading is read the title has
              scrolled off, and a bare "it" then points at nothing. The live
              field rather than `item.name`, so a rename at the top lands here
              on the keystroke; an empty field mid-edit falls back to the saved
              name, which autosave guarantees is not blank. */}
          <h3 className="card-title one-line" data-testid="facts-title">
            What's in {name.trim() || item.name}
          </h3>
          <NutritionPanel nutrition={item.nutrition} size={size} />
        </div>
      )}

      {/* ── 3. Are the details right? ────────────────────────────────────
          Always the facts. The controls open OVER them, in a popup, so the page
          never reflows under the reader and the values stay visible while they
          are being corrected. */}
      <div className="card facts">
        <div className="facts-head">
          <h3 className="card-title">Details</h3>
          <button
            type="button"
            className="ghost"
            onClick={() => openEdit(null)}
            data-testid="facts-toggle"
          >
            Edit
          </button>
        </div>

        <div className="summary" data-testid="facts-summary">
          <SummaryRow
            label="How much"
            value={size ? formatQuantity(size) : "Not set"}
            muted={!size}
            onEdit={() => openEdit("size")}
            flash={changed.has("size")}
            testId="row-size"
          />
          <SummaryRow
            label="Good until"
            value={expiry ? formatIsoDate(expiry, precision) : "No date"}
            muted={!expiry}
            /* Rendered at the precision the date is actually known to: a pack
                 read as "03/2028" shows as "Mar 2028", never as a day nobody
                 ever saw printed (AGENTS.md rule 2).

                 No "in 27 days" underneath it — the header already says that,
                 and saying it twice on one screen is the kind of padding that
                 makes a summary longer than the form it replaced. */
            onEdit={() => openEdit("expiry")}
            flash={changed.has("expiry")}
            testId="row-expiry"
          />
          <SummaryRow
            label="Where it lives"
            value={where}
            glyph={STORAGE_ICON[placeKind(storageId, places) ?? item.storage]}
            onEdit={() => openEdit("storage")}
            flash={changed.has("storage")}
            testId="row-storage"
          />
          <SummaryRow
            label="Kind of thing"
            value={category ? CATEGORY_LABEL[category] : "Not set"}
            muted={!category}
            glyph={category ? CATEGORY_EMOJI[category] : itemEmoji(emoji, category)}
            onEdit={() => openEdit("category")}
            flash={changed.has("category")}
            testId="row-category"
          />
          <SummaryRow
            label="What it cost"
            value={priceMinor === null ? "Not set" : formatMoney(priceMinor)}
            muted={priceMinor === null}
            /* The comparison people actually make in a shop. Absent unless
                 there is a size to divide by — see unitPrice. */
            note={
              priceMinor === null ? undefined : (unitPrice(priceMinor, size) ?? undefined)
            }
            onEdit={() => openEdit("price")}
            flash={changed.has("price")}
            testId="row-price"
          />
        </div>
      </div>

      {/* The edit popup.

          A scrim plus a panel, rather than a native `<dialog>`: the panel has to
          be a bottom sheet on a phone and a centred card on a desktop, and
          `showModal()` brings a top-layer element whose position this layout
          would have to fight. What `<dialog>` gives for free — Escape, and
          focus landing inside — is two effects above.

          Not rendered at all when closed, so nothing behind it can be tabbed
          into while it is open and the controls cost nothing when it is not. */}
      {editing && (
        <div className={`modal-root ${closing ? "out" : ""}`}>
          {/* Tapping outside closes. A button rather than a div so it is
              reachable without a pointer, and labelled because "close" is the
              only thing it does. */}
          <button
            type="button"
            className="scrim"
            aria-label="Close"
            onClick={closeEdit}
            data-testid="edit-scrim"
          />
          <div
            className="popup"
            role="dialog"
            aria-modal="true"
            aria-label="Edit details"
            tabIndex={-1}
            ref={popupRef}
            data-testid="edit-popup"
          >
            <header className="popup-head">
              <h3 className="card-title">Details</h3>
              {/* "Done" rather than "Close" or an ×: every change here has
                  already saved itself, so this dismisses a panel rather than
                  committing anything. The word people expect for that is
                  Done. */}
              <button
                type="button"
                className="ghost"
                onClick={closeEdit}
                data-testid="facts-done"
              >
                Done
              </button>
            </header>

            <div className="popup-body" ref={factsRef}>
              <fieldset className="group" data-field="size">
                <legend className="label">How much</legend>
                <QuantityField
                  value={size}
                  suggestion={guessQuantity(name)}
                  onChange={setSize}
                  idPrefix="edit-qty"
                />
                {/* Only when there IS one — the verbatim label text is provenance,
                  and provenance for a blank field is noise. */}
                {item.sizeText && (
                  <p className="note subtle" data-testid="size-text">
                    Pack says “{item.sizeText}”
                  </p>
                )}
              </fieldset>

              {/* ONE control holds the date: the date field. The chips beside it
                are SHORTCUTS, not a selection.

                They used to render as selected whenever the stored expiry
                happened to land exactly N days from today — which made the
                highlight an accident of the calendar. Open the same untouched
                item tomorrow and "5 days" is four days away, no chip matches,
                and the screen implies no date is set at all. */}
              <fieldset className="group" data-field="expiry">
                <legend className="label">Good until</legend>
                <input
                  type="date"
                  className="date-input"
                  value={expiry ?? ""}
                  min={addDays(today, -365 * 3)}
                  onChange={(e) => {
                    const next = isIsoDate(e.target.value) ? e.target.value : null;
                    setExpiry(next);
                    // A picker cannot express "March 2028", so anything chosen
                    // here is known to the day — and anything cleared is known to
                    // nothing.
                    setPrecision(next ? "day" : "none");
                  }}
                  data-testid="edit-date"
                />
                <p className="note" data-testid="edit-date-summary">
                  {expiry
                    ? describeDaysLeft(daysBetween(today, expiry))
                    : "No date — it stays on the list and never nags you."}
                </p>
                <p className="label subtle">{expiry ? "Move it to" : "Set it to"}</p>
                <div className="chips">
                  {QUICK_EXPIRY.map((q) => (
                    <button
                      key={q.label}
                      type="button"
                      className="chip"
                      onClick={() => {
                        setExpiry(addDays(today, q.days));
                        setPrecision("day");
                      }}
                      data-testid={`edit-expiry-${q.days}`}
                    >
                      {q.label}
                    </button>
                  ))}
                  {expiry && (
                    <button
                      type="button"
                      className="chip"
                      onClick={() => {
                        setExpiry(null);
                        setPrecision("none");
                      }}
                      data-testid="edit-expiry-none"
                    >
                      No date
                    </button>
                  )}
                </div>
              </fieldset>

              {places.length > 0 && (
                <fieldset className="group" data-field="storage">
                  <legend className="label">Where it lives</legend>
                  <div className="chips">
                    {places.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={`chip ${storageId === p.id ? "on" : ""}`}
                        onClick={() => setStorageId(p.id)}
                        data-testid={`edit-storage-${p.name}`}
                      >
                        {/* Decoration only — the name is right beside it. Same map
                          as the add screen, so a place looks the same wherever
                          it is offered. */}
                        <span className="chip-icon" aria-hidden="true">
                          {STORAGE_ICON[p.kind]}
                        </span>
                        {p.name}
                      </button>
                    ))}
                  </div>
                </fieldset>
              )}

              {/* Kind is offered but never demanded — it is filled in by
                enrichment and most people will never open this. It earns its
                place because a wrong category is the one enrichment error with a
                visible consequence: it picks the fallback glyph. */}
              <fieldset className="group" data-field="category">
                <legend className="label">Kind of thing</legend>
                <div className="chips">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`chip ${category === c ? "on" : ""}`}
                      onClick={() => setCategory(category === c ? null : c)}
                      data-testid={`edit-category-${c}`}
                    >
                      <span className="chip-icon" aria-hidden="true">
                        {CATEGORY_EMOJI[c]}
                      </span>
                      {CATEGORY_LABEL[c]}
                    </button>
                  ))}
                </div>
              </fieldset>

              {/* Optional, and the only field here nothing else can fill in.
                It earns the space because it is what turns "threw away 200 g of
                paneer" into "threw away ₹90" — the figure people feel. */}
              <fieldset className="group" data-field="price">
                <legend className="label">What it cost</legend>
                <div className="price-row">
                  <span className="price-symbol" aria-hidden="true">
                    ₹
                  </span>
                  <input
                    className="price-input"
                    type="text"
                    inputMode="decimal"
                    value={priceText}
                    placeholder="0"
                    aria-label="Price in rupees"
                    onChange={(e) => setPriceText(e.target.value)}
                    data-testid="edit-price"
                  />
                  {unitPrice(priceMinor ?? 0, size) && (
                    <span className="price-unit" data-testid="edit-price-unit">
                      {unitPrice(priceMinor ?? 0, size)}
                    </span>
                  )}
                </div>
              </fieldset>
            </div>

            <footer className="popup-foot">{status}</footer>
          </div>
        </div>
      )}

      {/* A reserved line rather than a conditional one: the status appears and
          disappears on its own, and letting it shove the cards below it around
          each time would be worse than the strip of air it costs. It lives in
          the popup's footer while that is open — one element, never two, or the
          page and the panel could disagree about whether a save had landed. */}
      <div className="save-slot">{!editing && status}</div>

      {/* ── 4. What has happened to it, and what is coming ───────────────
          The log has always been loaded and never shown. A verb and a date per
          line; anything longer is a changelog of your own groceries.

          It does not stop at the last event. The line runs on to the expiry,
          SOLID for the shelf life already spent and DOTTED for what is left, so
          "how far through is this?" is answered by a shape rather than by
          arithmetic on two dates. */}
      {history.length > 0 && (
        <div className="card" data-testid="history">
          <h3 className="card-title">Timeline</h3>
          <ol className="story">
            {history.map((h) => (
              <li key={h.id} className="story-row">
                <span className={`story-dot ${h.tone}`} aria-hidden="true" />
                <span className="story-text">{h.text}</span>
                <span className="story-when">{formatIsoDate(h.on)}</span>
              </li>
            ))}

            {/* Only while the item is still a live question. A packet that has
                been eaten has an ending already, and a date it never reached
                drawn below that ending would be a future for something that no
                longer has one. */}
            {life && (
              <>
                <li
                  className={`life ${life.urgency}`}
                  style={{ "--spent": `${life.spent * 100}%` } as CSSProperties}
                  aria-hidden="true"
                  data-testid="life"
                  data-spent={Math.round(life.spent * 100)}
                >
                  <span className="life-done" />
                  {/* Only when today is genuinely BETWEEN the two ends. At 0
                      or 100 the marker lands exactly on the dot it would be
                      marking, and two rings in one place read as a smudge
                      rather than as a position. */}
                  {life.spent > 0 && life.spent < 1 && <span className="life-now" />}
                </li>
                <li className="story-row">
                  <span
                    className={`story-dot future ${life.urgency}`}
                    aria-hidden="true"
                  />
                  <span className="story-text">Expiry</span>
                  <span className="story-when">{formatIsoDate(life.on, precision)}</span>
                </li>
              </>
            )}
          </ol>
        </div>
      )}

      {/* ── 5. Finished with it ──────────────────────────────────────────
          Pinned. Resolving an item is the one thing on this screen with a
          deadline attached, and on a page that now scrolls it must never be
          below the fold. Nothing else is fixed to the bottom here — the item
          screen renders without the nav bar. */}
      <div className="detail-actions">
        {/* "I'll want this again" is a thought people have while holding the
            packet, not only when the app happens to ask. The label names the
            DESTINATION, not the intention: "Buy this again" implies a purchase
            is about to happen, while this says exactly where the tap puts it. */}
        <button
          type="button"
          className="secondary slim"
          disabled={busy || alreadyCarted}
          onClick={onBuyAgain}
          data-testid="buy-again"
        >
          {alreadyCarted ? "✓ On your shopping list" : "🛒 Add to shopping list"}
        </button>
        <div className="checkin-actions">
          {/* The same two cues the check-in card uses, because they are the
              same two answers. See CheckIn.tsx and ui/cues.ts. */}
          <button
            type="button"
            className="answer good"
            disabled={busy}
            onClick={() => onAnswer("ate")}
            data-cue="ate"
            data-testid="detail-ate"
          >
            Ate it
          </button>
          <button
            type="button"
            className="answer bad"
            disabled={busy}
            onClick={() => onAnswer("tossed")}
            data-cue="tossed"
            data-testid="detail-tossed"
          >
            Threw it
          </button>
        </div>
      </div>
    </section>
  );
}

/**
 * One fact, and the way to change it.
 *
 * The whole row is the control, not a pencil icon at the end of it: a 44px
 * target the width of the screen is reachable one-handed, and the chevron says
 * it is tappable without spending a word on saying so.
 */
function SummaryRow({
  label,
  value,
  note,
  glyph,
  muted = false,
  flash = false,
  onEdit,
  testId,
}: {
  label: string;
  value: string;
  /** A derived figure that adds something the value cannot. One short line. */
  note?: string;
  glyph?: string;
  /** The value is a placeholder rather than a fact. */
  muted?: boolean;
  /**
   * This row differs from the packet just switched away from.
   *
   * The one thing worth animating on a switch. Everything else on the screen is
   * identical between two boxes of the same product, and animating the identical
   * parts is what made the old remount feel like a page load.
   */
  flash?: boolean;
  onEdit: () => void;
  testId: string;
}) {
  return (
    /* Spans, not a definition list. `dt`/`dd` cannot live inside a button, and
       the button has to be the outer element for the whole row to be the target
       — so the pairing is carried by the label sitting beside the value, which
       is also how it is announced. */
    <button
      type="button"
      className={`srow ${flash ? "changed" : ""}`}
      onClick={onEdit}
      data-testid={testId}
    >
      <span className="srow-label">{label}</span>
      <span className="srow-value">
        <span className={`srow-main ${muted ? "muted" : ""}`}>
          {glyph && (
            <span className="srow-glyph" aria-hidden="true">
              {glyph}
            </span>
          )}
          {value}
        </span>
        {note && <span className="srow-note">{note}</span>}
      </span>
      <span className="srow-go" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

/** The kind behind a named place, for its glyph. Null once a place is deleted. */
function placeKind(storageId: string | null, places: StoragePlace[]) {
  return (storageId ? places.find((p) => p.id === storageId)?.kind : null) ?? null;
}
